import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  Search,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Trash,
  Edit,
  Package,
  Settings,
  X,
  Filter,
  Eye,
  PlusCircle,
} from "lucide-react";
import { supabase } from "../../auth/SupabaseClient";

interface Category {
  id: string;
  name: string;
}

interface Size {
  id: string;
  name: string;
}

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  categories: Category[];
  stock: number;
  sku: string;
  created_at: string;
  updated_at: string;
  sizes: Size[];
}

const Products: React.FC = () => {
  const { darkMode } = useOutletContext<{ darkMode: boolean }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [sizes, setSizes] = useState<Size[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedSizeIds, setSelectedSizeIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<keyof Product>("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isGroupSelectionActive, setIsGroupSelectionActive] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const [isCategoryFilterOpen, setIsCategoryFilterOpen] = useState(false);
  const [isGroupActionDropdownOpen, setIsGroupActionDropdownOpen] =
    useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>("All");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);

  const navigate = useNavigate();

  const categoryFilterRef = useRef<HTMLDivElement>(null);
  const settingsButtonRef = useRef<HTMLDivElement>(null);
  const groupActionDropdownRef = useRef<HTMLDivElement>(null);
  const tableHeaderRef = useRef<HTMLTableSectionElement>(null);
  const productActionDropdownRef = useRef<HTMLDivElement>(null);

  const useClickOutside = (
    ref: React.RefObject<HTMLElement>,
    handler: () => void
  ) => {
    useEffect(() => {
      const listener = (event: MouseEvent | TouchEvent) => {
        if (!ref.current || ref.current.contains(event.target as Node)) {
          return;
        }
        handler();
      };

      document.addEventListener("mousedown", listener);
      document.addEventListener("touchstart", listener);

      return () => {
        document.removeEventListener("mousedown", listener);
        document.removeEventListener("touchstart", listener);
      };
    }, [ref, handler]);
  };

  useClickOutside(categoryFilterRef, () => setIsCategoryFilterOpen(false));
  useClickOutside(settingsButtonRef, () => setIsSubmenuOpen(false));
  useClickOutside(groupActionDropdownRef, () =>
    setIsGroupActionDropdownOpen(false)
  );
  useClickOutside(productActionDropdownRef, () => setOpenDropdown(null));

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: productsData, error: productsError } = await supabase
        .from("products")
        .select(`
          *,
          categories!products_category_id_fkey (
            id,
            name
          ),
          sizes!products_size_id_fkey (
            id,
            name
          )
        `)
        .order(sortField, { ascending: sortDirection === "asc" });

      if (productsError) throw productsError;

      if (!productsData) {
        throw new Error("No data returned from the products query");
      }

      // Transformation des données pour correspondre à l'interface Product
      const transformedProducts: Product[] = productsData.map((product: any) => ({
        ...product,
        category: product.categories ? product.categories : null,
        size: product.sizes ? product.sizes : null,
      }));

      setProducts(transformedProducts);
    } catch (error: any) {
      console.error("Error fetching products:", error);
      setError(
        "Une erreur est survenue lors de la récupération des produits. Veuillez réessayer."
      );
    } finally {
      setIsLoading(false);
    }
  }, [sortField, sortDirection]);

  const fetchCategories = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("categories")
        .select("id, name")
        .order("name");

      if (error) throw error;

      setCategories(data);
    } catch (error: any) {
      console.error("Error fetching categories:", error);
      setError(
        "Une erreur est survenue lors de la récupération des catégories. Veuillez réessayer."
      );
    }
  }, []);

  const fetchSizes = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("sizes")
        .select("*")
        .order("name");

      if (error) throw error;

      setSizes(data);
    } catch (error: any) {
      console.error("Error fetching sizes:", error);
      setError(
        "Une erreur est survenue lors de la récupération des tailles. Veuillez réessayer."
      );
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchSizes();
  }, [fetchProducts, fetchCategories, fetchSizes]);

  useEffect(() => {
    const handleScroll = () => {
      if (tableHeaderRef.current) {
        const { top } = tableHeaderRef.current.getBoundingClientRect();
        tableHeaderRef.current.style.position = top <= 0 ? "sticky" : "static";
        tableHeaderRef.current.style.top = top <= 0 ? "0" : "";
        tableHeaderRef.current.style.zIndex = top <= 0 ? "10" : "";
      }
    };

    const tableContainer = document.querySelector(".overflow-y-auto");
    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesSearch =
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        categoryFilter === "All" ||
        product.categories.some(
          (category) => category.name === categoryFilter
        );

      return matchesSearch && matchesCategory;
    });
  }, [products, searchTerm, categoryFilter]);

  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredProducts.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredProducts, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const handleSort = (field: keyof Product) => {
    setSortDirection(
      field === sortField && sortDirection === "asc" ? "desc" : "asc"
    );
    setSortField(field);
  };

  const handlePageChange = (page: number) => setCurrentPage(page);

  const toggleDropdown = (productId: string) => {
    setOpenDropdown(openDropdown === productId ? null : productId);
  };

  const handleDropdownAction = (action: string, productId: string) => {
    if (action === "Voir le produit") {
      navigate(`/dashboard/products/${productId}`);
    } else if (action === "Modifier") {
      const productToEdit = products.find((p) => p.id === productId);
      if (productToEdit) {
        setEditingProduct(productToEdit);
        setSelectedCategories(productToEdit.categories.map((cat) => cat.id));
        setSelectedSizes(productToEdit.sizes.map((size) => size.id));
        setIsAddProductModalOpen(true);
      }
    } else if (action === "Supprimer") {
      deleteProduct(productId);
    }
    setOpenDropdown(null);
  };

  const toggleGroupSelection = () => {
    if (isGroupSelectionActive) {
      setIsExiting(true);
      setTimeout(() => {
        setIsGroupSelectionActive(false);
        setSelectedProducts([]);
        setIsSubmenuOpen(false);
        setIsGroupActionDropdownOpen(false);
        setIsExiting(false);
      }, 300);
    } else {
      setIsGroupSelectionActive(true);
      setSelectedProducts([]);
      setIsSubmenuOpen(false);
    }
  };

  const toggleProductSelection = (productId: string) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const toggleAllSelection = () => {
    setSelectedProducts(
      selectedProducts.length === paginatedProducts.length
        ? []
        : paginatedProducts.map((product) => product.id)
    );
  };

  const handleBulkAction = (action: "delete" | "edit" | "export") => {
    console.log(`Bulk ${action} for products:`, selectedProducts);
    setIsGroupActionDropdownOpen(false);
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setSelectedCategories([]);
    setSelectedSizes([]);
    setIsAddProductModalOpen(true);
  };

  const toggleCategory = (categoryId: string) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const toggleSize = (sizeId: string) => {
    setSelectedSizes((prev) =>
      prev.includes(sizeId)
        ? prev.filter((id) => id !== sizeId)
        : [...prev, sizeId]
    );
  };

  const handleSaveProduct = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const productData = {
      name: formData.get("name") as string,
      description: formData.get("description") as string,
      price: parseFloat(formData.get("price") as string),
      stock: parseInt(formData.get("stock") as string),
      sku: formData.get("sku") as string,
    };

    try {
      setIsLoading(true);
      setError(null);

      if (editingProduct) {
        // Update existing product
        const { error } = await supabase
          .from("products")
          .update(productData)
          .eq("id", editingProduct.id);

        if (error) throw error;

        // Update product categories
        await supabase
          .from("product_categories")
          .delete()
          .eq("product_id", editingProduct.id);

        if (selectedCategories.length > 0) {
          await supabase.from("product_categories").insert(
            selectedCategories.map((categoryId) => ({
              product_id: editingProduct.id,
              category_id: categoryId,
            }))
          );
        }

        // Update product sizes
        await supabase
          .from("product_sizes")
          .delete()
          .eq("product_id", editingProduct.id);

        if (selectedSizes.length > 0) {
          await supabase.from("product_sizes").insert(
            selectedSizes.map((sizeId) => ({
              product_id: editingProduct.id,
              size_id: sizeId,
            }))
          );
        }
      } else {
        // Add new product
        const { data, error } = await supabase
          .from("products")
          .insert(productData)
          .select();

        if (error) throw error;

        const newProductId = data[0].id;

        // Add product categories
        if (selectedCategories.length > 0) {
          await supabase.from("product_categories").insert(
            selectedCategories.map((categoryId) => ({
              product_id: newProductId,
              category_id: categoryId,
            }))
          );
        }

        // Add product sizes
        if (selectedSizes.length > 0) {
          await supabase.from("product_sizes").insert(
            selectedSizes.map((sizeId) => ({
              product_id: newProductId,
              size_id: sizeId,
            }))
          );
        }
      }

      setIsAddProductModalOpen(false);
      fetchProducts();
    } catch (error: any) {
      console.error("Error saving product:", error);
      setError(
        "Une erreur est survenue lors de l'enregistrement du produit. Veuillez réessayer."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const deleteProduct = async (productId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Delete product categories
      await supabase
        .from("product_categories")
        .delete()
        .eq("product_id", productId);

      // Delete product sizes
      await supabase
        .from("product_sizes")
        .delete()
        .eq("product_id", productId);

      // Delete the product
      const { error } = await supabase
        .from("products")
        .delete()
        .eq("id", productId);

      if (error) throw error;

      fetchProducts();
    } catch (error: any) {
      console.error("Error deleting product:", error);
      setError(
        "Une erreur est survenue lors de la suppression du produit. Veuillez réessayer."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderSearchBar = () => (
    <div className="relative flex-grow md:mr-4">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
      <input
        type="text"
        placeholder="Rechercher..."
        className={`w-full pl-10 pr-4 py-2 rounded-lg ${
          darkMode ? "bg-gray-700 text-white" : "bg-gray-100 text-gray-800"
        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
    </div>
  );

  const renderCategoryFilter = () => (
    <div className="relative" ref={categoryFilterRef}>
      <button
        onClick={() => setIsCategoryFilterOpen(!isCategoryFilterOpen)}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
          darkMode
            ? "bg-gray-700 text-white hover:bg-gray-600"
            : "bg-gray-200 text-gray-800 hover:bg-gray-300"
        }`}
      >
        <Filter className="h-4 w-4 inline-block mr-2" />
        {categoryFilter === "All" ? "Toutes les catégories" : categoryFilter}
      </button>
      {isCategoryFilterOpen && (
        <div
          className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg z-20 ${
            darkMode ? "bg-gray-700" : "bg-white"
          } ring-1 ring-black ring-opacity-5 z-10`}
        >
          <div className="py-1" role="menu" aria-orientation="vertical">
            <button
              onClick={() => {
                setCategoryFilter("All");
                setIsCategoryFilterOpen(false);
              }}
              className={`block px-4 py-2 text-sm w-full text-left ${
                darkMode
                  ? "text-gray-300 hover:bg-gray-600"
                  : "text-gray-700 hover:bg-gray-100"
              } ${categoryFilter === "All" ? "bg-blue-500 text-white" : ""}`}
              role="menuitem"
            >
              Toutes les catégories
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => {
                  setCategoryFilter(category.name);
                  setIsCategoryFilterOpen(false);
                }}
                className={`block px-4 py-2 text-sm w-full text-left ${
                  darkMode
                    ? "text-gray-300 hover:bg-gray-600"
                    : "text-gray-700 hover:bg-gray-100"
                } ${
                  categoryFilter === category.name
                    ? "bg-blue-500 text-white"
                    : ""
                }`}
                role="menuitem"
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderSettingsButton = () => (
    <div className="relative" ref={settingsButtonRef}>
      <button
        onClick={() => setIsSubmenuOpen(!isSubmenuOpen)}
        className={`pt-1.5 pb-2 pr-3 pl-3 rounded-lg ${
          darkMode
            ? "bg-gray-700 text-white hover:bg-gray-600"
            : "bg-gray-200 text-gray-800 hover:bg-gray-300"
        }`}
      >
        <Settings className="h-4 w-4 inline-block" size={20} />
      </button>
      {isSubmenuOpen && (
        <div
          className={`z-20 absolute right-0 mt-2 w-48 rounded-md shadow-lg ${
            darkMode ? "bg-gray-700" : "bg-white"
          } ring-1 ring-black ring-opacity-5 z-10`}
        >
          <div className="py-1" role="menu" aria-orientation="vertical">
            <button
              onClick={() => {
                toggleGroupSelection();
                setIsSubmenuOpen(false);
              }}
              className={`block px-4 py-2 text-sm w-full text-left ${
                darkMode
                  ? "text-gray-300 hover:bg-gray-600"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
              role="menuitem"
            >
              <Package className="h-4 w-4 inline-block mr-2" />
              Sélection groupée
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderAddProductButton = () => (
    <button
      onClick={handleAddProduct}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
        darkMode
          ? "bg-blue-600 text-white hover:bg-blue-700"
          : "bg-blue-500 text-white hover:bg-blue-600"
      }`}
    >
      <PlusCircle className="h-4 w-4 inline-block mr-2" />
      Ajouter un produit
    </button>
  );

  const renderGroupActionButtons = () => (
    <div
      className={`flex items-center space-x-2 transition-all duration-300 ease-in-out pt-5 ${
        isExiting
          ? "opacity-0 transform -translate-y-2"
          : "opacity-100 transform translate-y-0 animate-fade-in-down"
      }`}
      style={{
        animation: isExiting ? "fadeOutUp 0.3s ease-out forwards" : "",
      }}
    >
      <div className="relative" ref={groupActionDropdownRef}>
        <button
          onClick={() =>
            setIsGroupActionDropdownOpen(!isGroupActionDropdownOpen)
          }
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Actions groupées ({selectedProducts.length})
          <ChevronDown className="h-4 w-4 inline-block ml-2" />
        </button>
        {isGroupActionDropdownOpen && (
          <div
            className={`absolute left-0 mt-2 w-48 rounded-md shadow-lg ${
              darkMode ? "bg-gray-700" : "bg-white"
            } ring-1 ring-black ring-opacity-5 z-20`}
          >
            <div className="py-1" role="menu" aria-orientation="vertical">
              {["delete", "edit", "export"].map((action) => (
                <button
                  key={action}
                  onClick={() =>
                    handleBulkAction(action as "delete" | "edit" | "export")
                  }
                  className={`block px-4 py-2 text-sm w-full text-left ${
                    darkMode
                      ? "text-gray-300 hover:bg-gray-600"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  role="menuitem"
                >
                  {action === "delete" && (
                    <Trash className="h-4 w-4 inline-block mr-2" />
                  )}
                  {action === "edit" && (
                    <Edit className="h-4 w-4 inline-block mr-2" />
                  )}
                  {action === "export" && (
                    <Package className="h-4 w-4 inline-block mr-2" />
                  )}
                  {action.charAt(0).toUpperCase() + action.slice(1)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <button
        onClick={toggleGroupSelection}
        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
      >
        <X className="h-4 w-4 inline-block mr-2" />
        Annuler
      </button>
    </div>
  );

  const handleRowClick = (productId: string) => {
    navigate(`/dashboard/products/${productId}`);
  };

  const renderProductTable = () => (
    <div className="flex-grow px-4 md:px-6">
      <div
        className={`overflow-x-auto overflow-y-auto max-h-[calc(100vh-600px)] min-h-[calc(100vh-350px)] rounded-lg border ${
          darkMode ? "border-gray-600" : "border-gray-300"
        }`}
      >
        <div className="inline-block min-w-full align-middle">
          <table
            className={`min-w-full rounded-lg ${
              darkMode ? "bg-gray-700" : "bg-white"
            }`}
          >
            <thead
              ref={tableHeaderRef}
              className={`${
                darkMode ? "bg-gray-800" : "bg-gray-100"
              } sticky top-0 z-10`}
            >
              <tr>
                {isGroupSelectionActive && (
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <input
                      type="checkbox"
                      checked={
                        selectedProducts.length === paginatedProducts.length
                      }
                      onChange={toggleAllSelection}
                      className={`form-checkbox h-5 w-5 ${
                        darkMode
                          ? "text-blue-500 border-gray-600 bg-gray-700"
                          : "text-blue-600 border-gray-300 bg-gray-100"
                      }`}
                    />
                  </th>
                )}
                {[
                  "name",
                  "sku",
                  "categories",
                  "price",
                  "stock",
                  "sizes",
                  "created_at",
                ].map((field) => (
                  <th
                    key={field}
                    scope="col"
                    className={`px-6 py-3 text-left text-xs font-medium ${
                      darkMode ? "text-gray-300" : "text-gray-500"
                    } uppercase tracking-wider cursor-pointer`}
                    onClick={() =>
                      field !== "sizes" && handleSort(field as keyof Product)
                    }
                  >
                    <div className="flex items-center">
                      {field === "name"
                        ? "Nom"
                        : field === "sku"
                        ? "SKU"
                        : field === "categories"
                        ? "Catégories"
                        : field === "price"
                        ? "Prix"
                        : field === "stock"
                        ? "Stock"
                        : field === "sizes"
                        ? "Tailles"
                        : field === "created_at"
                        ? "Date de création"
                        : field.charAt(0).toUpperCase() + field.slice(1)}
                      {sortField === field &&
                        field !== "sizes" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp className="ml-1 h-4 w-4" />
                        ) : (
                          <ChevronDown className="ml-1 h-4 w-4" />
                        ))}
                    </div>
                  </th>
                ))}
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody
              className={`${darkMode ? "bg-gray-900" : "bg-white"} divide-y ${
                darkMode ? "divide-gray-800" : "divide-gray-200"
              }`}
            >
              {paginatedProducts.map((product) => (
                <tr
                  key={product.id}
                  className={`cursor-pointer transition-colors duration-150 ease-in-out ${
                    darkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"
                  }`}
                >
                  {isGroupSelectionActive && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedProducts.includes(product.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleProductSelection(product.id);
                        }}
                        className={`form-checkbox h-5 w-5 ${
                          darkMode
                            ? "text-blue-500 border-gray-600 bg-gray-700"
                            : "text-blue-600 border-gray-300 bg-gray-100"
                        }`}
                      />
                    </td>
                  )}
                  <td
                    onClick={() => handleRowClick(product.id)}
                    className={`px-6 py-4 whitespace-nowrap ${
                      darkMode ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    {product.name}
                  </td>
                  <td
                    onClick={() => handleRowClick(product.id)}
                    className={`px-6 py-4 whitespace-nowrap ${
                      darkMode ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    {product.sku}
                  </td>
                  <td
                    onClick={() => handleRowClick(product.id)}
                    className={`px-6 py-4 whitespace-nowrap ${
                      darkMode ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    {product.categories && product.categories.length > 0
                      ? product.categories
                          .map((category) => category.name)
                          .join(", ")
                      : "Non disponible"}
                  </td>

                  <td
                    onClick={() => handleRowClick(product.id)}
                    className={`px-6 py-4 whitespace-nowrap ${
                      darkMode ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    {product.price.toFixed(2)} €
                  </td>
                  <td
                    onClick={() => handleRowClick(product.id)}
                    className={`px-6 py-4 whitespace-nowrap ${
                      darkMode ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    {product.stock}
                  </td>
                  <td
                    onClick={() => handleRowClick(product.id)}
                    className={`px-6 py-4 whitespace-nowrap ${
                      darkMode ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    {product.sizes && product.sizes.length > 0
                      ? product.sizes.map((size) => size.name).join(", ")
                      : "Non disponible"}
                  </td>
                  <td
                    onClick={() => handleRowClick(product.id)}
                    className={`px-6 py-4 whitespace-nowrap ${
                      darkMode ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    {new Date(product.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDropdown(product.id);
                      }}
                      className={`text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                        darkMode ? "hover:text-gray-300" : "hover:text-gray-700"
                      }`}
                    >
                      <MoreVertical className="h-5 w-5" />
                    </button>
                    {openDropdown === product.id && (
                      <div
                        className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg ${
                          darkMode ? "bg-gray-700" : "bg-white"
                        } ring-1 ring-black ring-opacity-5 z-10`}
                      >
                        <div
                          className="py-1"
                          role="menu"
                          aria-orientation="vertical"
                        >
                          {["Voir le produit", "Modifier", "Supprimer"].map(
                            (action) => (
                              <button
                                key={action}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDropdownAction(action, product.id);
                                }}
                                className={`block px-4 py-2 text-sm ${
                                  darkMode
                                    ? "text-gray-300 hover:bg-gray-600"
                                    : "text-gray-700 hover:bg-gray-100"
                                } w-full text-left`}
                                role="menuitem"
                              >
                                {action === "Voir le produit" && (
                                  <Eye className="inline-block mr-2 h-4 w-4" />
                                )}
                                {action === "Modifier" && (
                                  <Edit className="inline-block mr-2 h-4 w-4" />
                                )}
                                {action === "Supprimer" && (
                                  <Trash className="inline-block mr-2 h-4 w-4" />
                                )}
                                {action}
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {paginatedProducts.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64">
            <p
              className={`text-xl font-semibold ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Aucun produit trouvé
            </p>
            <p
              className={`mt-2 ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Essayez de modifier vos filtres ou votre recherche
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const renderPagination = () => (
    <div className="p-4 md:p-6">
      {filteredProducts.length > 0 ? (
        <div className="mt-4 flex flex-col md:flex-row items-center justify-between">
          <div className="flex items-center mb-4 md:mb-0">
            <span
              className={`mr-2 text-sm ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Résultats par page:
            </span>
            <select
              className={`px-2 py-1 rounded-lg ${
                darkMode
                  ? "bg-gray-700 text-white"
                  : "bg-gray-100 text-gray-800"
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              value={itemsPerPage.toString()}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
          <div className="flex items-center justify-between w-full md:w-auto">
            <p
              className={`text-sm ${
                darkMode ? "text-gray-300" : "text-gray-700"
              } mr-4`}
            >
              Affichage de {(currentPage - 1) * itemsPerPage + 1} à{" "}
              {Math.min(currentPage * itemsPerPage, filteredProducts.length)} sur{" "}
              {filteredProducts.length} résultats
            </p>
            <div className="flex space-x-2">
              <button
                className={`px-3 py-1 border rounded-md text-sm font-medium ${
                  darkMode
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50`}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    className={`px-3 py-1 border rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                      page === currentPage
                        ? "bg-blue-500 text-white border-blue-500"
                        : darkMode
                        ? "text-gray-300 border-gray-600 hover:bg-gray-700"
                        : "text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                )
              )}
              <button
                className={`px-3 py-1 border rounded-md text-sm font-medium ${
                  darkMode
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50`}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex flex-col md:flex-row items-center justify-between">
          <div className="flex items-center mb-4 md:mb-0">
            <span
              className={`mr-2 text-sm ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Résultats par page:
            </span>
            <select
              className={`px-2 py-1 rounded-lg ${
                darkMode
                  ? "bg-gray-700 text-white"
                  : "bg-gray-100 text-gray-800"
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              value={itemsPerPage.toString()}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
          <div className="flex items-center justify-between w-full md:w-auto">
            <p
              className={`text-sm ${
                darkMode ? "text-gray-300" : "text-gray-700"
              } mr-4`}
            >
              Aucun résultat trouvé
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const renderAddEditProductModal = () => (
    <div
      className={`fixed inset-0 z-50 overflow-y-auto ${
        isAddProductModalOpen ? "block" : "hidden"
      }`}
    >
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          className={`inline-block align-bottom ${
            darkMode ? "bg-gray-800" : "bg-white"
          } rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full`}
        >
          <form
            onSubmit={handleSaveProduct}
            className="space-y-8 max-w-3xl mx-auto"
          >
            <div
              className={`${
                darkMode ? "bg-gray-800" : "bg-white"
              } p-8 rounded-xl shadow-lg`}
            >
              <h3
                className={`text-2xl font-bold mb-8 ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {editingProduct
                  ? "Modifier le produit"
                  : "Ajouter un nouveau produit"}
              </h3>

              <div className="space-y-6">
                {/* Nom */}
                <div>
                  <label
                    htmlFor="name"
                    className={`block text-sm font-medium mb-2 ${
                      darkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Nom du produit
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    defaultValue={editingProduct?.name}
                    required
                    className={`w-full px-4 py-2 rounded-md ${
                      darkMode
                        ? "bg-gray-700 text-white"
                        : "bg-gray-50 text-gray-900"
                    } border ${
                      darkMode ? "border-gray-600" : "border-gray-300"
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 ease-in-out`}
                  />
                </div>

                {/* Description */}
                <div>
                  <label
                    htmlFor="description"
                    className={`block text-sm font-medium mb-2 ${
                      darkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={4}
                    defaultValue={editingProduct?.description}
                    className={`w-full px-4 py-2 rounded-md ${
                      darkMode
                        ? "bg-gray-700 text-white"
                        : "bg-gray-50 text-gray-900"
                    } border ${
                      darkMode ? "border-gray-600" : "border-gray-300"
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 ease-in-out`}
                  ></textarea>
                </div>

                {/* Prix et Stock */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="price"
                      className={`block text-sm font-medium mb-2 ${
                        darkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Prix
                    </label>
                    <div className="relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span
                          className={`${
                            darkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          €
                        </span>
                      </div>
                      <input
                        type="number"
                        id="price"
                        name="price"
                        step="0.01"
                        defaultValue={editingProduct?.price}
                        required
                        className={`w-full pl-7 pr-4 py-2 rounded-md ${
                          darkMode
                            ? "bg-gray-700 text-white"
                            : "bg-gray-50 text-gray-900"
                        } border ${
                          darkMode ? "border-gray-600" : "border-gray-300"
                        } focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 ease-in-out`}
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="stock"
                      className={`block text-sm font-medium mb-2 ${
                        darkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Stock
                    </label>
                    <input
                      type="number"
                      id="stock"
                      name="stock"
                      defaultValue={editingProduct?.stock}
                      required
                      className={`w-full px-4 py-2 rounded-md ${
                        darkMode
                          ? "bg-gray-700 text-white"
                          : "bg-gray-50 text-gray-900"
                      } border ${
                        darkMode ? "border-gray-600" : "border-gray-300"
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 ease-in-out`}
                    />
                  </div>
                </div>

                {/* Catégories */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      darkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Catégories
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => toggleCategory(category.id)}
                        className={`px-3 py-2 rounded-md text-sm font-medium ${
                          selectedCategories.includes(category.id)
                            ? darkMode
                              ? "bg-blue-600 text-white"
                              : "bg-blue-100 text-blue-800"
                            : darkMode
                            ? "bg-gray-700 text-gray-200"
                            : "bg-gray-200 text-gray-800"
                        } hover:bg-opacity-80 transition duration-200 ease-in-out`}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* SKU */}
                <div>
                  <label
                    htmlFor="sku"
                    className={`block text-sm font-medium mb-2 ${
                      darkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    SKU
                  </label>
                  <input
                    type="text"
                    id="sku"
                    name="sku"
                    defaultValue={editingProduct?.sku}
                    required
                    className={`w-full px-4 py-2 rounded-md ${
                      darkMode
                        ? "bg-gray-700 text-white"
                        : "bg-gray-50 text-gray-900"
                    } border ${
                      darkMode ? "border-gray-600" : "border-gray-300"
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 ease-in-out`}
                  />
                </div>

                {/* Tailles */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      darkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Tailles disponibles
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {sizes.map((size) => (
                      <button
                        key={size.id}
                        type="button"
                        onClick={() => toggleSize(size.id)}
                        className={`px-3 py-2 rounded-md text-sm font-medium ${
                          selectedSizes.includes(size.id)
                            ? darkMode
                              ? "bg-blue-600 text-white"
                              : "bg-blue-100 text-blue-800"
                            : darkMode
                            ? "bg-gray-700 text-gray-200"
                            : "bg-gray-200 text-gray-800"
                        } hover:bg-opacity-80 transition duration-200 ease-in-out`}
                      >
                        {size.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Boutons */}
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => setIsAddProductModalOpen(false)}
                  className={`px-6 py-2 rounded-md font-medium ${
                    darkMode
                      ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                      : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition duration-200 ease-in-out`}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className={`px-6 py-2 rounded-md text-white font-medium ${
                    darkMode
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-blue-500 hover:bg-blue-600"
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 ease-in-out`}
                >
                  {editingProduct ? "Modifier" : "Ajouter"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`rounded-lg flex flex-col min-h-[84%] ${
        darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
      }`}
    >
      <div className="p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          {renderSearchBar()}
          <div className="flex items-center space-x-4">
            {renderCategoryFilter()}
            {renderSettingsButton()}
            {renderAddProductButton()}
          </div>
        </div>
        {(isGroupSelectionActive || isExiting) && renderGroupActionButtons()}
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="text-center text-red-500 mt-4">{error}</div>
      ) : (
        renderProductTable()
      )}

      {renderPagination()}
      {renderAddEditProductModal()}
    </div>
  );
};

export default Products;
